import './AppContainer.css';
import { useAuthContext } from './auth/AuthStateProvider';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { stopImpersonating } from './server-api/api/user';

export interface AppContainerProps {
  children: ReactNode;
}

interface NavLink {
  href: string;
  label: string;
  match: (path: string) => boolean;
  separatorBefore?: boolean;
}

const NAV_LINKS: NavLink[] = [
  { href: '/', label: 'Dashboard', match: p => p === '/' },
  { href: '/wine', label: 'Wine', match: p => p.startsWith('/wine') },
  { href: '/spirits', label: 'Spirits', match: p => p.startsWith('/spirits') },
  { href: '/cocktails', label: 'Cocktails', match: p => p === '/cocktails' || p.startsWith('/cocktail') || p.startsWith('/ingredient') },
  { href: '/cellar', label: 'Cellar', match: p => p.startsWith('/cellar'), separatorBefore: true },
  { href: '/menu', label: 'Menus', match: p => p.startsWith('/menu') },
];

const SearchIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="7" cy="7" r="4.5" />
    <path d="m10.5 10.5 3 3" />
  </svg>
);

const ProfileIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
    <circle cx="8" cy="6" r="2.6" />
    <path d="M3 13.5c.9-2.3 2.8-3.5 5-3.5s4.1 1.2 5 3.5" />
  </svg>
);

// Dashboard-style four-tile glyph. Bottom-left tile gets a diagonal cut so the
// icon reads as "admin control panel" rather than a generic grid.
const AdminIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="0.8">
    <rect x="3" y="3" width="4.5" height="4.5" rx="0.7" />
    <rect x="8.5" y="3" width="4.5" height="4.5" rx="0.7" />
    <rect x="8.5" y="8.5" width="4.5" height="4.5" rx="0.7" />
    <rect x="3" y="8.5" width="4.5" height="4.5" rx="0.7" />
    {/* <path d="M3 8.5 L7.5 8.5 L7.5 13 L5 13 Z" /> */}
  </svg>
);

export const AppContainer: React.FC<AppContainerProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuthContext();
  const path = window.location.pathname;
  const [searchOpen, setSearchOpen] = useState(false);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);

  // Allow shareable / public pages to opt out of the chrome by setting
  // `body.classList.add('share-page')` from the page itself. We listen for the
  // attribute on first render only; pages mount after this so we re-check on
  // every render.
  const isSharePage = typeof document !== 'undefined' && document.body.classList.contains('share-page');

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => {
          const el = document.getElementById('app-search-input') as HTMLInputElement | null;
          el?.focus();
          el?.select();
        }, 60);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (isSharePage) {
    return <>{children}</>;
  }

  return (
    <div className="app">
      {user?.impersonating && (
        <div
          role="status"
          style={{
            background: 'var(--ink)',
            color: 'var(--paper)',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            fontSize: 13,
            position: 'sticky',
            top: 0,
            zIndex: 40,
          }}
        >
          <span>
            Impersonating <strong>{user.name}</strong> ({user.email})
          </span>
          <button
            className="btn"
            onClick={async () => {
              setStoppingImpersonation(true);
              try {
                await stopImpersonating();
              } finally {
                window.location.href = '/admin/users';
              }
            }}
            disabled={stoppingImpersonation}
            style={{ height: 28, background: 'var(--paper)', color: 'var(--ink)' }}
          >
            {stoppingImpersonation ? '…' : 'Stop impersonating'}
          </button>
        </div>
      )}
      <header className="topnav" aria-label="Sections">
        <div className="topnav__row topnav__row--main">
          <a href="/" className="brand" aria-label="Bar Almanac home">Bar&nbsp;Almanac</a>
          <div className="topnav__links">
            {NAV_LINKS.map(link => (
              <Fragment key={link.href}>
                {link.separatorBefore && <span className="topnav__sep" aria-hidden="true" />}
                <a
                  className="topnav__link"
                  href={link.href}
                  aria-current={link.match(path) ? 'page' : undefined}
                >
                  {link.label}
                </a>
              </Fragment>
            ))}
          </div>
          <button
            className="icon-btn topnav__search-btn"
            aria-label="Search"
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen(o => !o)}
          >
            <SearchIcon />
          </button>
          {isAuthenticated ? (
            <>
              <a
                href="/profile"
                className="icon-btn"
                aria-label="Profile"
                aria-current={path.startsWith('/profile') ? 'page' : undefined}
                style={{ marginLeft: 6 }}
              >
                <ProfileIcon />
              </a>
              {(user as any)?.role === 'admin' && (
                <a
                  href="/admin/users"
                  className="icon-btn"
                  aria-label="Admin"
                  aria-current={path.startsWith('/admin') ? 'page' : undefined}
                  style={{ marginLeft: 6 }}
                  title="Admin panel"
                >
                  <AdminIcon />
                </a>
              )}
            </>
          ) : (
            <a
              href="/login"
              className="btn"
              style={{ marginLeft: 6, height: 34 }}
            >
              Sign in
            </a>
          )}
        </div>
        <div className="topnav__divider" />
        <div className={`topnav__search ${searchOpen ? 'open' : ''}`}>
          <div className="topnav__search-wrap">
            <label className="search" aria-label="Search the bar program">
              <span className="search__icon"><SearchIcon /></span>
              <input
                id="app-search-input"
                type="search"
                placeholder="Search spirits, recipes, vintages…"
                autoComplete="off"
                onKeyDown={e => { if (e.key === 'Escape') setSearchOpen(false); }}
              />
              <span className="search__hint hide-sm">Esc</span>
            </label>
          </div>
        </div>
      </header>
      <main className="page">
        {children}
      </main>
    </div>
  );
};
